import React, { RefObject } from 'react';
import {Calendar, Users, Mail, ArrowRight } from 'lucide-react';

interface IntroSectionProps {
  visibleSections: Set<string>;
  introSectionRef: RefObject<HTMLElement>;
}

const IntroSection: React.FC<IntroSectionProps> = ({ visibleSections, introSectionRef }) => {
  const quickStats = [
    { icon: Calendar, label: 'Event Planning', value: 'Simplified' },
    { icon: Users, label: 'Guest Management', value: 'Automated' },
    { icon: Mail, label: 'Invitations', value: 'Streamlined' },
  ];

  return (
    <section
      id="intro"
      ref={introSectionRef}
      className="relative z-10 bg-black overflow-hidden"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 pointer-events-none z-0">
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1f1f1f_1px,transparent_1px),linear-gradient(to_bottom,#1f1f1f_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-20" />
      </div>

      {/* Main content */}
      <div className="relative z-10 py-8 sm:py-10 md:py-12 lg:py-14 px-4 sm:px-6 lg:px-8 mt-12 sm:mt-14 md:mt-16 lg:mt-6">
        <div className="max-w-7xl mx-auto">
          {/* Two-column layout */}
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-center relative min-h-[300px] sm:min-h-[350px] lg:min-h-[450px]">
            {/* Left Column - Text Content */}
            <div
              className={`relative z-20 transition-all duration-1000 delay-300 mt-16 ${
                visibleSections.has('intro')
                  ? 'opacity-100 translate-x-0'
                  : 'opacity-0 -translate-x-10'
              }`}
            >
              {/* Main headline */}
              <h2
                className={`font-megant text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-5 transition-all duration-1000 delay-500 ${
                  visibleSections.has('intro')
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-10'
                }`}
              >
                <span className="font-megant block bg-gradient-to-r from-white via-green-400 to-white bg-clip-text text-transparent leading-tight pb-1 mb-2">
                 Eliminating the chaos of
                  event guest management
                </span>
              </h2>

              {/* Description */}
              <div className="font-montserrat space-y-3 mb-5 sm:mb-6">
                <div className="h-1 w-16 bg-gradient-to-r from-green-400 to-transparent" />
                <p className="font-montserrat text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed">
                  Everyone suffers the same struggle when planning an event, getting lost between the
                  design, attire, guest lists and so on. Yet, most don't even think of invites and
                  attendees and leave that to the last minute.
                </p>
                <p className="font-montserrat text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed">
                  That's where <span className="font-montserrat text-green-400 font-semibold">MIRSAL</span> comes in.
                  We focus on covering all aspects of customized invite management, ensuring your event
                  planning is seamless from start to finish.
                </p>
              </div>

              {/* Quick stats cards */}
              <div
                className={`grid sm:grid-cols-3 gap-3 sm:gap-4 mb-5 sm:mb-6 transition-all duration-1000 delay-700 ${
                  visibleSections.has('intro')
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-10'
                }`}
              >
                {quickStats.map((stat, index) => {
                  const Icon = stat.icon;
                  return (
                    <div
                      key={index}
                      className="group bg-gradient-to-br from-gray-900/40 to-gray-900/20 rounded-xl border border-gray-800/50 p-3 sm:p-4 hover:border-green-400/30 hover:shadow-lg hover:shadow-green-400/10 transition-all duration-300 hover:-translate-y-1"
                      style={{
                        transitionDelay: visibleSections.has('intro') ? `${800 + index * 100}ms` : '0ms',
                      }}
                    >
                      <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 mb-2 rounded-lg bg-green-500/10 border border-green-500/20 group-hover:bg-green-500/20 group-hover:scale-110 transition-all duration-300">
                        <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                      </div>
                      <div className="text-center">
                        <div className="font-montserrat text-lg sm:text-xl font-bold text-white mb-0.5 group-hover:text-green-400 transition-colors">
                          {stat.value}
                        </div>
                        <div className="font-montserrat text-xs text-gray-400">{stat.label}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* CTA Button */}
              <div
                className={`transition-all duration-1000 delay-900 ${
                  visibleSections.has('intro')
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-5'
                }`}
              >
                <a
                  href="#how-it-works"
                  className="font-montserrat group inline-flex items-center gap-2 px-5 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-green-500/20 to-green-600/20 border border-green-400/30 rounded-full text-white text-sm sm:text-base font-medium hover:from-green-500/30 hover:to-green-600/30 hover:border-green-400/50 transition-all duration-300 hover:scale-105"
                >
                  <span className="font-montserrat">Discover How It Works</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>

            {/* Right Column - Visual Element */}
            <div
              className={`absolute lg:relative right-0 top-1/2 lg:top-auto -translate-y-1/2 lg:translate-y-0 w-full lg:w-auto h-[300px] sm:h-[350px] lg:h-[450px] blur-sm lg:blur-0 opacity-40 lg:opacity-100 z-10 transition-all duration-1000 delay-500 ${
                visibleSections.has('intro')
                  ? 'translate-x-0'
                  : 'opacity-0 translate-x-10'
              }`}
            >
              {/* Abstract blob shapes */}
              <div className="absolute inset-0">
                {/* Large blob 1 */}
                <div
                  className="absolute top-10 right-10 w-64 h-64 sm:w-80 sm:h-80 bg-gradient-to-br from-green-500/30 to-green-600/20 rounded-full blur-3xl animate-pulse"
                  style={{ animationDelay: '0s' }}
                />
                
                {/* Medium blob 2 */}
                <div
                  className="absolute bottom-20 left-10 w-48 h-48 sm:w-64 sm:h-64 bg-gradient-to-br from-green-400/25 to-green-500/15 rounded-full blur-2xl animate-pulse"
                  style={{ animationDelay: '1s' }}
                />
                
                {/* Small blob 3 */}
                <div
                  className="absolute top-1/2 right-1/4 w-32 h-32 sm:w-40 sm:h-40 bg-gradient-to-br from-green-600/20 to-green-400/10 rounded-full blur-xl animate-pulse"
                  style={{ animationDelay: '0.5s' }}
                />

                {/* Organic blob shapes using SVG */}
                <svg
                  className="absolute inset-0 w-full h-full"
                  viewBox="0 0 400 600"
                  preserveAspectRatio="xMidYMid meet"
                >
                  {/* Blob shape 1 */}
                  <path
                    d="M250,150 Q280,120 320,140 T350,180 Q340,200 320,220 T280,240 Q260,230 250,210 T250,150"
                    fill="rgba(34, 197, 94, 0.2)"
                    className="animate-pulse"
                    style={{ animationDelay: '0.3s' }}
                  />
                  
                  {/* Blob shape 2 */}
                  <path
                    d="M100,350 Q120,320 150,340 T180,380 Q170,400 150,420 T120,440 Q100,430 100,410 T100,350"
                    fill="rgba(74, 222, 128, 0.25)"
                    className="animate-pulse"
                    style={{ animationDelay: '0.7s' }}
                  />
                  
                  {/* Blob shape 3 */}
                  <path
                    d="M300,450 Q330,420 360,440 T380,480 Q370,500 350,520 T320,540 Q300,530 300,510 T300,450"
                    fill="rgba(34, 197, 94, 0.15)"
                    className="animate-pulse"
                    style={{ animationDelay: '1.2s' }}
                  />
                </svg>

                {/* Device mockup - Phone */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div
                    className={`relative w-40 h-80 sm:w-48 sm:h-96 transition-all duration-1000 delay-700 ${
                      visibleSections.has('intro')
                        ? 'opacity-100 scale-100'
                        : 'opacity-0 scale-95'
                    }`}
                  >
                    {/* Phone frame */}
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 rounded-[2.5rem] p-2 shadow-2xl border-2 border-gray-700">
                      {/* Phone screen */}
                      <div className="w-full h-full bg-gradient-to-br from-gray-900 to-black rounded-[2rem] overflow-hidden relative">
                        {/* Screen content - abstract representation */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
                          {/* Mirsal Logo */}
                          <div className="mb-4 flex items-center justify-center">
                            <img 
                              src="/mirsal_word.png" 
                              alt="Mirsal Logo" 
                              className="w-24 h-auto sm:w-28 opacity-90"
                            />
                          </div>
                          {/* App icon grid representation */}
                          <div className="grid grid-cols-3 gap-2 mt-2">
                            {[1, 2, 3, 4, 5, 6].map((i) => (
                              <div
                                key={i}
                                className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-green-500/30 to-green-600/20 border border-green-400/30"
                              />
                            ))}
                          </div>
                          {/* Glow effect */}
                          <div className="absolute inset-0 bg-gradient-to-b from-green-500/10 via-transparent to-transparent" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Glow effect around phone */}
                    <div className="absolute inset-0 -z-10 bg-green-500/20 rounded-[2.5rem] blur-2xl scale-110 animate-pulse" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section Divider */}
      <div className="relative z-10 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div
            className={`h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent transition-all duration-1000 delay-1200 ${
              visibleSections.has('intro')
                ? 'opacity-100 scale-x-100'
                : 'opacity-0 scale-x-0'
            }`}
          />
        </div>
      </div>
    </section>
  );
};

export default IntroSection;